import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { categoryLabels, colors, pick, PRODUCT_CATEGORIES, PRODUCT_ICONS, type ProductCategory, type ProductIcon } from '@hogar/shared';
import { Button, Chip, ErrorText, Field, Label, Screen, confirm, useT } from '../components/ui';
import { BackHeader } from '../components/BackHeader';
import { productIcons } from '../components/icons';
import { api } from '../lib/api';
import { useAction } from '../lib/session';
import { useHouse } from '../lib/house';

/** Add a supply, or rename, recategorize, or delete one. */
export default function ProductEdit() {
  const { id, category: startCategory, crew } = useLocalSearchParams<{ id?: string; category?: ProductCategory; crew?: string }>();
  const { t, lang } = useT();
  const { products } = useHouse();
  const act = useAction();
  const product = id ? products.find((p) => p.id === id) : undefined;
  const [name, setName] = useState(product ? pick(product.name, lang) : '');
  const [category, setCategory] = useState<ProductCategory>(product?.category ?? startCategory ?? 'general');
  const [icon, setIcon] = useState<ProductIcon>(product?.icon ?? 'package');
  // English-speaking owners can correct the Spanish staff see.
  const [nameEs, setNameEs] = useState(product?.name.src === 'en' ? product.name.es : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => (router.canGoBack() ? router.back() : router.replace('/productos'));

  const save = async () => {
    setBusy(true);
    try {
      const fields = { name: name.trim(), category, icon };
      if (product) await act(() => api('PATCH', `/products/${product.id}`, { ...fields, ...(product.name.src === 'en' && nameEs.trim() && nameEs.trim() !== product.name.es ? { nameEs: nameEs.trim() } : {}) }));
      else await act(() => api('POST', '/products', { ...fields, crew: crew === 'garden' ? 'garden' : 'house' }));
      close();
    } catch {
      setError(t('errGeneric'));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!product || !(await confirm(t('deleteProduct') + '?', t('delete'), t('cancel')))) return;
    await act(() => api('DELETE', `/products/${product.id}`));
    close();
  };

  return (
    <Screen>
      <BackHeader title={product ? t('edit') : t('addProduct')} fallback="/productos" />
      <Label>{t('name')}</Label>
      <Field value={name} onChange={setName} autoFocus={!product} />
      {product && lang === 'en' && product.name.src === 'en' && (
        <>
          <Label>{t('spanishForStaff')}</Label>
          <Field value={nameEs} onChange={setNameEs} />
        </>
      )}
      <Label>{t('category')}</Label>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {PRODUCT_CATEGORIES.filter((c) => ((product?.crew ?? crew) === 'garden') === (c === 'garden')).map((c) => (
          <Chip key={c} label={categoryLabels[c][lang]} on={c === category} onPress={() => setCategory(c)} />
        ))}
      </View>
      <Label>{t('icon')}</Label>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        {PRODUCT_ICONS.map((key) => {
          const Icon = productIcons[key];
          const on = key === icon;
          return (
            <Pressable
              key={key}
              onPress={() => setIcon(key)}
              style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? colors.primary : colors.card, borderWidth: 1, borderColor: on ? colors.primary : colors.cardBorder }}
            >
              <Icon size={22} color={on ? colors.white : colors.primary} strokeWidth={1.6} />
            </Pressable>
          );
        })}
      </View>
      <ErrorText>{error}</ErrorText>
      <Button label={t('save')} onPress={save} disabled={busy || !name.trim()} style={{ marginTop: 22 }} />
      {product && <Button label={t('deleteProduct')} variant="danger" onPress={remove} style={{ marginTop: 10 }} />}
    </Screen>
  );
}
